import { useMemo } from 'react';
import type { ChartData, ChartOptions, TooltipItem } from 'chart.js';

import BaseChart from './BaseChart';
import { type ChartTheme, useChartTheme } from './theme';

/** One slice of a pie or doughnut. */
export interface CircularSlice {
    /** Already translated. */
    label: string;
    value: number;
    /** A palette colour; drawn as the chart theme has it. */
    color: string;
}

/**
 * Tooltip text for the hovered slice. Either function falls back to the Chart.js default
 * (title: the slice label, line: the value) when left out.
 */
export interface CircularTooltip {
    title?: (slice: CircularSlice) => string;
    label?: (slice: CircularSlice) => string;
}

export interface CircularChartProps {
    kind: 'pie' | 'doughnut';
    slices: CircularSlice[];
    /** Doughnut hole, in px or as a percentage string. */
    cutout?: number | string;
    /**
     * Defaults to the Chart.js tooltip. `false` turns the tooltip and every hover effect off,
     * for a chart that only illustrates numbers printed beside it.
     */
    tooltip?: CircularTooltip | false;
    /**
     * Defaults to `true`: slices are parted by a 2px line in the card colour, as Chart.js
     * parts them in white. `false` draws them flush.
     */
    separated?: boolean;
    /** Animates the slices sweeping in, as well as growing. */
    animateRotate?: boolean;
    /** Already translated. Names the chart for screen readers, with the slice labels. */
    title?: string;
}

const SLICE_GAP = 2;

function sliceAt(slices: CircularSlice[], item: TooltipItem<'pie' | 'doughnut'>) {
    return slices[item.dataIndex];
}

function tooltipOptions(
    slices: CircularSlice[],
    tooltip: CircularTooltip | false | undefined,
    theme: ChartTheme
) {
    if (tooltip === false) return { enabled: false };
    const title = tooltip?.title;
    const label = tooltip?.label;
    return {
        enabled: true,
        backgroundColor: theme.tooltip.bg,
        titleColor: theme.tooltip.text,
        bodyColor: theme.tooltip.text,
        borderColor: theme.tooltip.border,
        borderWidth: 1,
        callbacks: {
            ...(title
                ? {
                      title: (items: TooltipItem<'pie' | 'doughnut'>[]) => {
                          const slice = items[0] ? sliceAt(slices, items[0]) : undefined;
                          return slice ? title(slice) : '';
                      },
                  }
                : {}),
            ...(label
                ? {
                      label: (item: TooltipItem<'pie' | 'doughnut'>) => {
                          const slice = sliceAt(slices, item);
                          return slice ? label(slice) : '';
                      },
                  }
                : {}),
        },
    };
}

/**
 * The app's pie and doughnut. Slices are drawn in their palette colour as the chart theme has
 * it, parted by the card colour, so the chart sits on a dark card the way it sits on a white
 * one. Legends stay with the caller, which knows how its numbers should read.
 */
export default function CircularChart(props: CircularChartProps) {
    const { kind, slices, cutout, tooltip, separated = true, animateRotate, title } = props;
    const theme = useChartTheme();

    const data = useMemo(
        () => ({
            labels: slices.map((slice) => slice.label),
            datasets: [
                {
                    data: slices.map((slice) => slice.value),
                    backgroundColor: slices.map((slice) => theme.color(slice.color)),
                    borderColor: theme.surface,
                    borderWidth: separated ? SLICE_GAP : 0,
                },
            ],
        }),
        [slices, separated, theme]
    );

    const options = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: true,
            ...(cutout === undefined ? {} : { cutout }),
            ...(animateRotate === undefined ? {} : { animation: { animateRotate } }),
            // Without events the chart cannot be hovered, so no slice grows under the cursor.
            ...(tooltip === false ? { events: [] } : {}),
            plugins: {
                legend: { display: false },
                tooltip: tooltipOptions(slices, tooltip, theme),
            },
        }),
        [slices, cutout, animateRotate, tooltip, theme]
    );

    if (kind === 'doughnut') {
        const doughnutData: ChartData<'doughnut'> = data;
        const doughnutOptions: ChartOptions<'doughnut'> = options;
        return (
            <BaseChart
                type="doughnut"
                data={doughnutData}
                options={doughnutOptions}
                title={title}
            />
        );
    }
    const pieData: ChartData<'pie'> = data;
    const pieOptions: ChartOptions<'pie'> = options;
    return <BaseChart type="pie" data={pieData} options={pieOptions} title={title} />;
}
